import { Link, PageProps, graphql } from 'gatsby'
import { getSrc } from 'gatsby-plugin-image'
import React from 'react'
import { PaddedContainer } from '../components/container'
import Layout from '../components/layout'
import FeaturedPageHeader from '../components/molecules/featuredPageHeader'
import SEO from '../components/molecules/seo'
import { ContentSet } from '../components/organisms/contentSet'
import PageCategoryGroupContentSet from '../components/organisms/pageCategoryGroupContentSet'
import PageCategoryGroupContentSetWithImages from '../components/organisms/pageCategoryGroupContentSetWithImages'
import PageHeader from '../components/organisms/pageHeader'
import { GreyBackground } from '../pages/whats-on'

export const Head: React.FC<
  PageProps<Queries.PageCategoryTemplateQuery, PageCategoryTemplateContextTypes>
> = ({ data, pageContext }) => {
  const { title } = pageContext
  const featuredPage = data?.featuredPage?.edges[0]?.node
  const { heroImage, leadingParagraph } = featuredPage ?? {}

  return (
    <SEO
      title={title}
      description={leadingParagraph?.childMarkdownRemark?.rawMarkdownBody}
      imageUrl={heroImage && getSrc(heroImage)}
    />
  )
}

type PageCategoryTemplateContextTypes = {
  title: string
  pageCategorySlug: string
  pageCategory: {
    title: string
    slug: string
    leadingParagraph: {
      childMarkdownRemark: {
        rawMarkdownBody: string
      }
    }
    pageCategoryGroups: [
      {
        id: string
        title: string
        hideTitle: boolean
        showFeaturedImages: boolean
        links: [
          {
            title: string
            slug: string
            leadingParagraph: {
              childMarkdownRemark: {
                rawMarkdownBody: string
              }
            }
          },
        ]
      },
    ]
  }
  pageCategoryFeeds: [
    {
      title: string
      slug: string
      allLinkSlug: string
      feedEdges: [{ node }]
    },
  ]
}
const PageCategoryTemplate: React.FC<
  PageProps<Queries.PageCategoryTemplateQuery, PageCategoryTemplateContextTypes>
> = ({ data, pageContext }) => {
  const { title, pageCategory, pageCategoryFeeds } = pageContext
  const { slug, leadingParagraph, pageCategoryGroups } = pageCategory
  const featuredPage = data?.featuredPage?.edges[0]?.node

  // Assuming feedA and feedB are objects with node properties that have viewDate and createdAt properties of type Date
  function compare(
    feedA: { node: { viewDate: Date | null; createdAt: Date } },
    feedB: { node: { viewDate: Date | null; createdAt: Date } },
  ): number {
    // Use the nullish coalescing operator (??) to assign a default value if viewDate is null
    const timeA = feedA.node.viewDate ?? feedA.node.createdAt
    const timeB = feedB.node.viewDate ?? feedB.node.createdAt
    // Use the comparison operators (< and >) to return -1, 1, or 0
    return timeA < timeB ? -1 : timeA > timeB ? 1 : 0
  }

  // Assuming data is an object with pageCategoryParent property that has slug and title properties of type string
  // Assuming title is a string variable
  // Assuming Link is a component that takes a to prop of type string
  const renderBreadcrumbs = (data: {
    pageCategoryParent?: { slug: string; title: string }
  }): JSX.Element => {
    return (
      <>
        <Link to='/'>Home /</Link>
        {data.pageCategoryParent && (
          <Link to={`/${data.pageCategoryParent.slug}/`}>
            {' '}
            {data.pageCategoryParent.title} /
          </Link>
        )}{' '}
        {title}
      </>
    )
  }

  return (
    <Layout>
      <PageHeader
        data={{
          title: title,
          slug: slug,
          leadingParagraph: leadingParagraph,
        }}
        breadcrumbs={renderBreadcrumbs(pageCategory)}
      />
      <GreyBackground>
        {featuredPage && (
          <FeaturedPageHeader
            page={featuredPage}
            categorySlug={slug}
            pageCategoryParent={pageCategory.pageCategoryParent}
          />
        )}
        <PaddedContainer>
          {pageCategoryGroups?.map((pageCategoryGroup) => {
            const {
              links,
              hideTitle,
              showFeaturedImages,
              title: pageCategoryTitle,
            } = pageCategoryGroup

            if (showFeaturedImages === null || showFeaturedImages === false) {
              return (
                <PageCategoryGroupContentSet
                  title={pageCategoryTitle}
                  hideTitle={hideTitle}
                  nodes={links}
                  slug={slug}
                  key={`${slug}-${pageCategoryTitle}`}
                />
              )
            }

            if (showFeaturedImages && showFeaturedImages === true) {
              return (
                <PageCategoryGroupContentSetWithImages
                  title={pageCategoryTitle}
                  hideTitle={hideTitle}
                  nodes={links}
                  slug={slug}
                  key={`${slug}-${pageCategoryTitle}`}
                />
              )
            }
          })}
          {(slug === 'stories' || slug === 'news') &&
            pageCategoryFeeds &&
            pageCategoryFeeds.map((pageCategoryFeed) => {
              return (
                <PageCategoryGroupContentSetWithImages
                  key={`${slug}-${pageCategoryFeed.title}`}
                  title={pageCategoryFeed.title}
                  hideTitle={true}
                  nodes={pageCategoryFeed.feedEdges}
                  slug={slug}
                />
              )
            })}
        </PaddedContainer>
        {slug !== 'stories' &&
          slug !== 'news' &&
          pageCategoryFeeds &&
          pageCategoryFeeds.map((pageCategoryFeed) => {
            return (
              <ContentSet
                key={`${slug}-${pageCategoryFeed.title}`}
                title={pageCategoryFeed.title}
                allLink={pageCategoryFeed.allLinkSlug}
                nodes={pageCategoryFeed.feedEdges}
              />
            )
          })}
      </GreyBackground>
    </Layout>
  )
}

export default PageCategoryTemplate

export const pageQuery = graphql`
  query PageCategoryTemplate($pageCategorySlug: String) {
    featuredPage: allContentfulPage(
      filter: { category: { slug: { eq: $pageCategorySlug } }, isFeatured: { eq: true } }
      sort: { updatedAt: DESC }
      limit: 1
    ) {
      edges {
        node {
          title
          slug
          leadingParagraph {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, quality: 80)
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, quality: 80)
            }
          }
          category {
            slug
            pageCategoryParent {
              slug
            }
          }
        }
      }
    }
  }
`
